import { CircularProgress } from "@mui/material";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { useEffect } from "react";

import { useSaveSeed, useSeed } from "@/api/creativesphere";
import { photographyCodec } from "@/api/creativesphere/codecs/photography";
import { PhotographyPresetBuilder } from "@/features/GenLab/components/PhotographyPresetBuilder";
import {
  describePreset,
  type PhotographyPreset,
} from "@/features/GenLab/components/photographyRigs";

export const Route = createFileRoute(
  "/_auth/genlab/$siteId/presets/edit/$presetId",
)({
  component: EditPresetRoute,
});

function EditPresetRoute() {
  const { siteId, presetId } = Route.useParams();
  const navigate = useNavigate();
  const { data, isLoading, isError } = useSeed(siteId, presetId);
  const saveSeed = useSaveSeed(siteId);

  const goBackToStudio = () => {
    void navigate({ to: "/genlab/$siteId", params: { siteId } });
  };

  useEffect(() => {
    if (isError) goBackToStudio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);

  if (isLoading) return <CircularProgress />;
  if (!data) return null;

  const preset = photographyCodec.fromSeed(data);

  const handleSave = (changes: {
    name: string;
    settings: PhotographyPreset["settings"];
  }) => {
    saveSeed.mutate(
      {
        slug: preset.id,
        body: {
          seed_type: photographyCodec.seedType,
          name: changes.name,
          description: describePreset(changes.settings),
          params: photographyCodec.toParams(changes.settings),
        },
      },
      {
        onSuccess: () => {
          enqueueSnackbar("Information saved", { variant: "success" });
          goBackToStudio();
        },
        onError: () => {
          enqueueSnackbar("Couldn't save changes. Please try again.", {
            variant: "error",
          });
        },
      },
    );
  };

  return (
    <PhotographyPresetBuilder
      preset={preset}
      onSave={handleSave}
      onBack={goBackToStudio}
    />
  );
}
